import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Leaf, Phone, Mail, FileText, CheckCircle } from 'lucide-react';
import { db } from '../firebase';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import './TermsAndConditions.css';

function TermsAndConditions() {
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get('cid');
  const [recorded, setRecorded] = useState(false);

  // Record that this client viewed the T&C
  useEffect(() => {
    if (!clientId) return;

    const recordView = async () => {
      try {
        const clientRef = doc(db, 'clients', clientId);
        const snap = await getDoc(clientRef);
        if (snap.exists()) {
          await updateDoc(clientRef, {
            termsViewed: true,
            termsViewedAt: new Date().toISOString()
          });
          setRecorded(true);
        }
      } catch (err) {
        console.error('Failed to record T&C view:', err);
      }
    };

    recordView();
  }, [clientId]);

  return (
    <div className="terms-page">
      <div className="terms-page-container">
        {/* Header */}
        <div className="terms-page-header">
          <div className="terms-page-logo">
            <div className="terms-page-logo-icon"><Leaf size={24} /></div>
            <div>
              <h1>Two Sisters</h1>
              <span>Cleaning & Organizing</span>
            </div>
          </div>
        </div>

        {/* Content Card */}
        <div className="terms-page-card">
          {/* Viewed Confirmation */}
          {recorded && (
            <div className="terms-viewed-banner">
              <CheckCircle size={16} />
              <span>Thank you for reviewing our terms and conditions.</span>
            </div>
          )}

          {/* Title */}
          <div className="terms-page-title-section">
            <div className="terms-page-title-icon"><FileText size={22} /></div>
            <h2>Terms & Conditions</h2>
            <p>Please review our service agreement below. By scheduling a service with Two Sisters Cleaning & Organizing, you agree to these terms.</p>
          </div>

          {/* Section: Service Agreement */}
          <div className="terms-section">
            <h3>1. Service Agreement</h3>
            <p>By booking a cleaning service with Two Sisters Cleaning & Organizing LLC ("we," "us," "our"), you ("the client") agree to the following terms and conditions. These terms apply to all residential cleaning services including recurring, deep clean, and move-in/move-out services.</p>
          </div>

          {/* Section: Scheduling & Access */}
          <div className="terms-section">
            <h3>2. Scheduling & Access</h3>
            <ul>
              <li>Appointments are scheduled based on mutual availability and confirmed via email and/or text message.</li>
              <li>The client is responsible for providing safe and reasonable access to the home at the scheduled time (e.g., lockbox code, garage code, key under mat, or being home to let us in).</li>
              <li>If we are unable to access the home at the scheduled time, the appointment may be marked as a late cancellation and the cancellation fee will apply.</li>
            </ul>
          </div>

          {/* Section: Cancellation & Rescheduling */}
          <div className="terms-section">
            <h3>3. Cancellation & Rescheduling</h3>
            <ul>
              <li><strong>48-hour notice</strong> is required for cancellations or rescheduling without penalty.</li>
              <li>Cancellations made with less than 48 hours' notice will be subject to a <strong>cancellation fee of 50%</strong> of the scheduled service cost.</li>
              <li>No-shows or same-day cancellations will be charged the <strong>full service amount</strong>.</li>
              <li>We understand emergencies happen. Please communicate with us as soon as possible and we'll do our best to work with you.</li>
            </ul>
          </div>

          {/* Section: Pricing & Payment */}
          <div className="terms-section">
            <h3>4. Pricing & Payment</h3>
            <ul>
              <li>Service pricing is based on the estimate provided and agreed upon before the first visit. Estimates are based on the size, condition, and specific needs of the home.</li>
              <li>All cleaning services have a minimum charge of <strong>$125</strong>.</li>
              <li><strong>New residential clients are required to pay in full</strong> before their appointment date to secure their appointment.</li>
              <li>Payment is due upon completion of each service unless other arrangements have been made.</li>
              <li>We accept <strong>card payments only</strong>. Cash may be left as a tip for your cleaning team but cannot be applied toward service charges.</li>
              <li>Recurring service rates are based on the agreed-upon frequency. Changes to frequency may result in a rate adjustment.</li>
              <li>Prices may be adjusted periodically with advance notice.</li>
            </ul>
          </div>

          {/* Section: Scope of Service */}
          <div className="terms-section">
            <h3>5. Scope of Service</h3>
            <ul>
              <li>Each service type (recurring, deep clean, move-in/move-out) has a standard scope of work outlined in your estimate and on our <a href="/scope">Scope of Service</a> page.</li>
              <li>Additional tasks outside the standard scope may be requested and may incur additional charges.</li>
              <li>We reserve the right to decline tasks that pose safety risks or fall outside of our service capabilities (e.g., biohazard cleanup, exterior cleaning, heavy lifting).</li>
            </ul>
          </div>

          {/* Section: Satisfaction & Feedback */}
          <div className="terms-section">
            <h3>6. Satisfaction & Feedback</h3>
            <ul>
              <li>Your satisfaction is our priority. If you are not satisfied with any aspect of our service, please notify us <strong>within 24 hours</strong> of the completed cleaning.</li>
              <li>We will return to address the specific areas of concern at no additional cost within a reasonable timeframe.</li>
              <li>Requests made after the 24-hour window may be addressed at our discretion.</li>
            </ul>
          </div>

          {/* Section: Home Conditions & Preparation */}
          <div className="terms-section">
            <h3>7. Home Conditions & Preparation</h3>
            <ul>
              <li>For the safety of our team, we ask that homes be in a reasonably tidy condition before our arrival (e.g., dishes put away, floors clear of personal items, trash emptied).</li>
              <li>Excessively cluttered or unsanitary conditions beyond the agreed scope may require additional time and cost, or we may need to reschedule.</li>
              <li>Please secure valuables, fragile items, and heirlooms before our visit. While we take the utmost care, we are not responsible for items left in cleaning areas that are easily breakable or of high sentimental value.</li>
            </ul>
          </div>

          {/* Section: Pets */}
          <div className="terms-section">
            <h3>8. Pets</h3>
            <ul>
              <li>Please let us know about any pets in the home when booking your service.</li>
              <li>We kindly ask that pets be secured or managed during the cleaning to ensure the safety of both our team and your pets.</li>
              <li>We are not responsible for pets that escape through open doors during service.</li>
            </ul>
          </div>

          {/* Section: Breakage & Liability */}
          <div className="terms-section">
            <h3>9. Breakage & Liability</h3>
            <ul>
              <li>We take great care in your home, but accidents can happen. If something is damaged during a cleaning, please notify us within 24 hours.</li>
              <li>We carry general liability insurance. Verified claims will be handled promptly and fairly.</li>
              <li>We are not liable for damage caused by pre-existing conditions (e.g., loose fixtures, worn surfaces, improper previous repairs).</li>
              <li>Items of extraordinary value should be disclosed in advance so we can take special precautions or exclude them from the cleaning area.</li>
            </ul>
          </div>

          {/* Section: Key & Security */}
          <div className="terms-section">
            <h3>10. Key & Security Information</h3>
            <ul>
              <li>Access codes, keys, and security information provided to us are kept strictly confidential and stored securely.</li>
              <li>Please update us immediately if access codes or security systems change.</li>
              <li>We will re-lock and re-arm security systems upon departure as instructed.</li>
            </ul>
          </div>

          {/* Section: Recurring Services */}
          <div className="terms-section">
            <h3>11. Recurring Service Agreements</h3>
            <ul>
              <li>Recurring services (weekly, bi-weekly, monthly) are offered at discounted rates based on the agreed frequency.</li>
              <li>To pause or cancel recurring services, we require <strong>one full service cycle's notice</strong> (e.g., two weeks for bi-weekly clients).</li>
              <li>Extended pauses (more than 6 weeks) may result in a rate adjustment upon resuming, as the home may require additional time to return to the recurring maintenance level.</li>
              <li>Holiday schedules and closures will be communicated in advance.</li>
            </ul>
          </div>

          {/* Section: Privacy */}
          <div className="terms-section">
            <h3>12. Privacy & Communication</h3>
            <ul>
              <li>We respect your privacy. Personal information is used solely for providing and improving our services.</li>
              <li>We may communicate with you via email, text, or phone regarding appointments, reminders, invoices, and service updates.</li>
              <li>Photos of your home will never be taken without your explicit consent.</li>
            </ul>
          </div>

          {/* Section: Modifications */}
          <div className="terms-section">
            <h3>13. Changes to Terms</h3>
            <p>We reserve the right to update these terms and conditions at any time. Clients will be notified of any significant changes via email. Continued use of our services after notification constitutes acceptance of the updated terms.</p>
          </div>

          {/* Effective Date */}
          <div className="terms-effective-date">
            <p><strong>Effective Date:</strong> February 2025</p>
            <p>Two Sisters Cleaning & Organizing LLC &bull; Charlottesville, VA</p>
          </div>

          {/* CTA */}
          <div className="terms-page-cta">
            <p>Have questions about our terms?</p>
            <div className="terms-page-contact">
              <a href="tel:+14342072246"><Phone size={16} /> (434) 207-2246</a>
              <a href="mailto:hello@twosisterscleaningco.com"><Mail size={16} /> hello@twosisterscleaningco.com</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TermsAndConditions;
